/**
 * buyerMatchScore.js
 * ─────────────────────────────────────────────────────────────────────────────
 * Pure score logic — no React, no API calls, no side effects.
 * Takes a listing object (shape from the portal's HomeCard/Details data) and
 * a buyerProfile object (shape from contextStore's buyerHomeDetails) and
 * returns a detail score breakdown.
 *
 * SCORE WEIGHTS (total max = 90% without AI layer):
 *   Price match          20pts
 *   Beds match           15pts
 *   Baths match          10pts
 *   Property type match  10pts
 *   City/location match  15pts
 *   ──────────────────────────
 *   House criteria total 80pts  (Tier 1)
 *
 *   Work address         8pts   (Tier 2, life addresses)
 *   School locations     7pts   (Tier 2, life addresses)
 *   Activity locations   5pts   (Tier 2, life addresses)
 *   ──────────────────────────
 *   Life address total   20pts
 *
 *   AI personality layer 10pts  (Tier 3, separate Haiku call)
 *   ──────────────────────────
 *   Grand total         100pts
 *
 * Distances use the Haversine formula (straight-line, no API cost) for
 * Tier 1 city matching and Tier 2 commute scoring. Accurate driving
 * distances are computed separately on the detail page via Google Distance
 * Matrix and fed in as overrides.
 */

// ── Haversine straight-line distance (miles) ─────────────────────────────────
function haversineDistance(lat1, lon1, lat2, lon2) {
  if (!lat1 || !lon1 || !lat2 || !lon2) return null;
  const R = 3958.8; // Earth radius in miles
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

// ── Clamp helper ──────────────────────────────────────────────────────────────
function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

// ── TIER 1: House criteria scoring (80 pts max) ────────────────────────────────

function scorePriceTier1(listing, profile) {
  const MAX = 20;
  var rawMin = profile.minprice || profile.minPrice || 0;
  var rawMax = profile.maxprice || profile.maxPrice || null;
  var rawIdeal = profile.idealPrice || null;
  var minPrice = parseFloat(String(rawMin).replace(/[^0-9.]/g, "")) || 0;
  var maxPrice = rawMax ? parseFloat(String(rawMax).replace(/[^0-9.]/g, "")) : null;
  var idealPrice = rawIdeal ? parseFloat(String(rawIdeal).replace(/[^0-9.]/g, "")) : null;
  var listPrice = parseFloat(
    String(listing.listPrice || listing.cost || listing.OriginalListprice || listing.minPrice || 0).replace(/[^0-9.]/g, "")
  );
  if (!listPrice || isNaN(listPrice)) return { score: 0, max: MAX, label: "Price", detail: "No listing price available" };

  // If no range set at all
  if (!minPrice && !maxPrice && !idealPrice) {
    return { score: Math.round(MAX * 0.5), max: MAX, label: "Price", detail: "No price preference set" };
  }

  // Score against ideal price if set — proximity-based
  if (idealPrice && !isNaN(idealPrice)) {
    var diff = Math.abs(listPrice - idealPrice) / idealPrice;
    if (diff <= 0.05) return { score: MAX, max: MAX, label: "Price", detail: `$${listPrice.toLocaleString()} is near your ideal price` };
    if (diff <= 0.10) return { score: Math.round(MAX * 0.85), max: MAX, label: "Price", detail: `$${listPrice.toLocaleString()} is close to your ideal price` };
    if (diff <= 0.20) return { score: Math.round(MAX * 0.65), max: MAX, label: "Price", detail: `$${listPrice.toLocaleString()} is reasonably close to your ideal` };
    if (diff <= 0.35) return { score: Math.round(MAX * 0.40), max: MAX, label: "Price", detail: `$${listPrice.toLocaleString()} is somewhat outside your ideal` };
    return { score: Math.round(MAX * 0.10), max: MAX, label: "Price", detail: `$${listPrice.toLocaleString()} is well outside your ideal price` };
  }

  // Fall back to range scoring
  var effectiveMax = maxPrice || (minPrice * 2);
  if (listPrice >= minPrice && listPrice <= effectiveMax) {
    var mid = (minPrice + effectiveMax) / 2;
    var range = (effectiveMax - minPrice) / 2 || 1;
    var proximity = 1 - Math.abs(listPrice - mid) / range;
    var score = Math.round(MAX * (0.8 + 0.2 * proximity));
    return { score, max: MAX, label: "Price", detail: `$${listPrice.toLocaleString()} is within your budget` };
  }
  var overage = listPrice > effectiveMax
    ? (listPrice - effectiveMax) / effectiveMax
    : minPrice > 0 ? (minPrice - listPrice) / minPrice : 0;
  if (overage <= 0.05) return { score: Math.round(MAX * 0.6), max: MAX, label: "Price", detail: "Slightly outside your price range" };
  if (overage <= 0.15) return { score: Math.round(MAX * 0.3), max: MAX, label: "Price", detail: "Outside your price range" };
  return { score: 0, max: MAX, label: "Price", detail: "Well outside your price range" };
}

function scoreBedsTier1(listing, profile) {
  const MAX = 15;
  var minBeds = parseInt(profile.minbeds || profile.beds || 0) || 0;
  var rawIdealBeds = profile.idealBeds || profile.idealBed || 0;
  var idealBeds = parseInt(String(rawIdealBeds).replace(/[^0-9]/g, "")) || 0;
  var listBeds = parseInt(listing.beds || 0) || 0;

  if (!minBeds && !idealBeds) return { score: MAX, max: MAX, label: "Bedrooms", detail: "No bedroom preference set" };
  if (!listBeds) return { score: 0, max: MAX, label: "Bedrooms", detail: "Bedroom count not specified" };

  if (idealBeds) {
    if (listBeds === idealBeds) return { score: MAX, max: MAX, label: "Bedrooms", detail: `${listBeds} beds matches your ideal` };
    if (listBeds > idealBeds) return { score: Math.round(MAX * 0.85), max: MAX, label: "Bedrooms", detail: `${listBeds} beds exceeds your ideal of ${idealBeds}` };
    if (listBeds === idealBeds - 1) return { score: Math.round(MAX * 0.8), max: MAX, label: "Bedrooms", detail: `${listBeds} beds, you ideally want ${idealBeds}` };
    if (listBeds === idealBeds - 2) return { score: Math.round(MAX * 0.6), max: MAX, label: "Bedrooms", detail: `${listBeds} beds, you ideally want ${idealBeds}` };
    if (listBeds >= minBeds) return { score: Math.round(MAX * 0.4), max: MAX, label: "Bedrooms", detail: `${listBeds} beds meets minimum but not ideal` };
    return { score: 0, max: MAX, label: "Bedrooms", detail: `${listBeds} beds is too few` };
  }

  if (listBeds >= minBeds) {
    var bonus = Math.min(listBeds - minBeds, 2);
    var score = Math.round(clamp(MAX + bonus, 0, MAX));
    return { score, max: MAX, label: "Bedrooms", detail: `${listBeds} beds meets your minimum of ${minBeds}` };
  }
  var shortage = minBeds - listBeds;
  if (shortage === 1) return { score: Math.round(MAX * 0.4), max: MAX, label: "Bedrooms", detail: `${listBeds} beds, you wanted ${minBeds}+` };
  return { score: 0, max: MAX, label: "Bedrooms", detail: `${listBeds} beds is too few (you need ${minBeds}+)` };
}

function scoreBathsTier1(listing, profile) {
  const MAX = 10;
  var minBaths = parseFloat(profile.minbaths || profile.baths || 0) || 0;
  var rawIdealBaths = profile.idealBaths || profile.idealBath || 0;
  var idealBaths = parseFloat(String(rawIdealBaths).replace(/[^0-9.]/g, "")) || 0;
  var listBaths = parseFloat(listing.baths || 0) + (listing.halfBaths > 0 ? 0.5 : 0);

  if (!minBaths && !idealBaths) return { score: MAX, max: MAX, label: "Bathrooms", detail: "No bathroom preference set" };
  if (!listBaths) return { score: 0, max: MAX, label: "Bathrooms", detail: "Bathroom count not specified" };

  if (idealBaths) {
    if (listBaths >= idealBaths) return { score: MAX, max: MAX, label: "Bathrooms", detail: `${listBaths} baths meets your ideal of ${idealBaths}` };
    if (listBaths === idealBaths - 0.5) return { score: Math.round(MAX * 0.85), max: MAX, label: "Bathrooms", detail: `${listBaths} baths is close to your ideal of ${idealBaths}` };
    if (listBaths === idealBaths - 1) return { score: Math.round(MAX * 0.7), max: MAX, label: "Bathrooms", detail: `${listBaths} baths, you ideally want ${idealBaths}` };
    if (listBaths >= minBaths) return { score: Math.round(MAX * 0.5), max: MAX, label: "Bathrooms", detail: `${listBaths} baths meets minimum but not ideal` };
    return { score: 0, max: MAX, label: "Bathrooms", detail: `${listBaths} baths is too few` };
  }

  if (listBaths >= minBaths) return { score: MAX, max: MAX, label: "Bathrooms", detail: `${listBaths} baths meets your minimum of ${minBaths}` };
  var shortage = minBaths - listBaths;
  if (shortage <= 0.5) return { score: Math.round(MAX * 0.5), max: MAX, label: "Bathrooms", detail: `Close on baths: ${listBaths} vs ${minBaths} needed` };
  return { score: 0, max: MAX, label: "Bathrooms", detail: `${listBaths} baths is too few (you need ${minBaths}+)` };
}
function scoreLivingAreaTier1(listing, profile) {
  const MAX = 10;
  var minArea = parseFloat(profile.minArea || profile.minSqft || 0) || 0;
  var maxArea = parseFloat(profile.maxArea || profile.maxSqft || 0) || 0;
  var idealArea = parseFloat(profile.idealArea || profile.idealSqft || 0) || 0;
  var listArea = parseFloat(listing.area || listing.LivingArea || 0) || 0;
  if (!minArea && !maxArea && !idealArea) return { score: Math.round(MAX * 0.5), max: MAX, label: "Living Area", detail: "No size preference set" };
  if (!listArea) return { score: Math.round(MAX * 0.5), max: MAX, label: "Living Area", detail: "Square footage not listed" };
  if (idealArea) {
    var diff = Math.abs(listArea - idealArea) / idealArea;
    if (diff <= 0.05) return { score: MAX, max: MAX, label: "Living Area", detail: `${listArea.toLocaleString()} sqft matches your ideal` };
    if (diff <= 0.15) return { score: Math.round(MAX * 0.8), max: MAX, label: "Living Area", detail: `${listArea.toLocaleString()} sqft is close to your ideal` };
    if (diff <= 0.30) return { score: Math.round(MAX * 0.6), max: MAX, label: "Living Area", detail: `${listArea.toLocaleString()} sqft is near your ideal size` };
    if (listArea >= (minArea || 0)) return { score: Math.round(MAX * 0.3), max: MAX, label: "Living Area", detail: `${listArea.toLocaleString()} sqft meets minimum but not ideal` };
    return { score: 0, max: MAX, label: "Living Area", detail: `${listArea.toLocaleString()} sqft is too small` };
  }
  if (minArea && listArea < minArea) return { score: 0, max: MAX, label: "Living Area", detail: `${listArea.toLocaleString()} sqft is below your minimum` };
  if (maxArea && listArea > maxArea) return { score: Math.round(MAX * 0.4), max: MAX, label: "Living Area", detail: `${listArea.toLocaleString()} sqft is larger than you need` };
  return { score: Math.round(MAX * 0.7), max: MAX, label: "Living Area", detail: `${listArea.toLocaleString()} sqft is within your range` };
}
function scorePropertyTypeTier1(listing, profile) {
  const MAX = 10;
  const preferredTypes = profile.subTypeText || profile.propertyType || profile.propertyTypes || [];
  var rawType = listing.type || listing.PropertyType || listing.labels || "";
var SIMPLYRETS_ALIASES = {
  "Single Family Residence": "Residential Single Family Home",
  "Single Family": "Residential Single Family Home",
  "Condominium": "Residential Condo",
  "Condominium/Co-op": "Residential Condo",
  "Multi Family": "Residential Multi Family Home",
  "Residential": "Residential Single Family Home",
  "Commercial": "Commercial MLS",
  "Land": "Land",
  "Mobile Home": "Mobile Homes",
};
var listType = SIMPLYRETS_ALIASES[rawType] || rawType;
  if (!preferredTypes || preferredTypes.length === 0) {
    return { score: MAX, max: MAX, label: "Property Type", detail: "No type preference set" };
  }
  if (!listType) return { score: Math.round(MAX * 0.5), max: MAX, label: "Property Type", detail: "Property type not specified" };

  // Map MLS type codes to display names for comparison
  const MLS_TYPE_MAP = {
    RES: ["Residential Single Family Home", "Residential Studio", "Residential Apartment", "Residential"],
    CND: ["Residential Condo", "Residential Condominium", "Condominium/Co-op"],
    MLF: ["Residential Multi Family Home"],
    RNT: ["Residential Rental"],
    CRE: ["Commercial MLS", "Commercial Office", "Commerical/Industrial", "Business Opportunity"],
    LND: ["Land", "Commercial Land", "Residential Land"],
    MBL: ["Mobile Homes"],
  };
  const expandedList = preferredTypes.flatMap((t) => MLS_TYPE_MAP[t] || [t]);
  const matches = expandedList.some(
    (t) => listType.toLowerCase().includes(t.toLowerCase()) || t.toLowerCase().includes(listType.toLowerCase())
  );
  if (matches) return { score: MAX, max: MAX, label: "Property Type", detail: `${listType} matches your preference` };
  return { score: 0, max: MAX, label: "Property Type", detail: `${listType} doesn't match your preferred types` };
}

function scoreCityTier1(listing, profile) {
  const MAX = 15;
  var rawCities = profile.updatedInterestedTowns || [];
  var targetCities = profile.targetCities || [];
  if (targetCities.length > 0 && rawCities.length === 0) {
    rawCities = targetCities.map(function(c) {
      var parts = c.split(",");
      return { cityName: parts[0].trim(), stateName: (parts[1] || "MA").trim() };
    });
  }
  const preferredCities = rawCities;
  const listCity = (listing.city || "").toLowerCase().trim();
  const listState = (listing.stateCode || listing.stateName || listing.state || "").toLowerCase().trim();

  if (!preferredCities || preferredCities.length === 0) {
    return { score: MAX, max: MAX, label: "Location", detail: "No city preference set" };
  }
  if (!listCity) return { score: Math.round(MAX * 0.5), max: MAX, label: "Location", detail: "Listing city not specified" };

  const exactMatch = preferredCities.some(
    (c) =>
      c.cityName.toLowerCase().trim() === listCity &&
      (c.stateName.toLowerCase() === listState || listState === "")
  );
  if (exactMatch) return { score: MAX, max: MAX, label: "Location", detail: `${listing.city} is one of your preferred towns` };

  // Partial — listing is in a preferred state but not exact city
  const stateMatch = preferredCities.some((c) => c.stateName.toLowerCase() === listState);
  if (stateMatch) return { score: Math.round(MAX * 0.4), max: MAX, label: "Location", detail: `Same state as preferred areas (not an exact city match)` };

  return { score: 0, max: MAX, label: "Location", detail: "Not in your preferred areas" };
}

// ── TIER 2: Life address scoring (20pts max) ──────────────────────────────────
// Uses Haversine straight-line distance for card badges.
// listingCoords: { lat, lng } — from listing's geocoded address
// lifeAddresses: array of { type, lat, lng, label, maxCommuteMiles }
//   type: "work" | "school" | "activity"

function scoreLifeAddressesTier2(listingCoords, lifeAddresses, drivingDistances) {
  const WEIGHTS = { work: 8, school: 7, activity: 5 };
  const MAX_TOTAL = 20;

  if (!lifeAddresses || lifeAddresses.length === 0) {
    return { score: 0, max: MAX_TOTAL, label: "Life Address Match", detail: "No life addresses added yet", breakdown: [] };
  }
  if (!listingCoords || !listingCoords.lat || !listingCoords.lng) {
    return { score: 0, max: MAX_TOTAL, label: "Life Address Match", detail: "Listing location not available", breakdown: [] };
  }

  // Group by type to distribute weights
  const byType = { work: [], school: [], activity: [] };
  lifeAddresses.forEach((a) => {
    if (byType[a.type]) byType[a.type].push(a);
  });

  let totalScore = 0;
  const breakdown = [];

  Object.entries(byType).forEach(([type, addresses]) => {
    if (addresses.length === 0) return;
    const maxPtsPerType = WEIGHTS[type];

    const typeScores = addresses.map((addr, idx) => {
      if (!addr.lat || !addr.lng) return { pct: 0, label: addr.label, detail: "Location not geocoded" };

      // Use driving distance if available (detail page), else straight-line
      const distKey = `${type}_${idx}`;
      const miles = drivingDistances && drivingDistances[distKey] != null
        ? drivingDistances[distKey]
        : haversineDistance(listingCoords.lat, listingCoords.lng, addr.lat, addr.lng);

      if (miles === null) return { pct: 0, label: addr.label, detail: "Distance unavailable" };

      const maxMiles = addr.maxCommuteMiles || 30;
      let pct;
      if (miles <= maxMiles * 0.5) pct = 1.0;          // Within half preferred commute = perfect
      else if (miles <= maxMiles) pct = 0.7;            // Within preferred commute = good
      else if (miles <= maxMiles * 1.5) pct = 0.3;     // Up to 1.5x = partial
      else pct = 0;                                      // Too far

      const milesLabel = miles < 1 ? "<1 mi" : `${Math.round(miles)} mi`;
      const driveLabel = drivingDistances && drivingDistances[distKey] != null ? "(driving)" : "(est.)";
      return {
        pct,
        label: addr.label || `${type} location`,
        detail: `${milesLabel} ${driveLabel} — ${miles <= maxMiles ? "within" : "exceeds"} your ${maxMiles}mi commute preference`,
      };
    });

    // Average across multiple addresses of same type
    const avgPct = typeScores.reduce((sum, s) => sum + s.pct, 0) / typeScores.length;
    const typeScore = Math.round(maxPtsPerType * avgPct);
    totalScore += typeScore;

    breakdown.push({
      type,
      score: typeScore,
      max: maxPtsPerType,
      addresses: typeScores,
    });
  });

  const detail = breakdown.length > 0
    ? breakdown.map((b) => `${b.type}: ${b.score}/${b.max}pts`).join(" · ")
    : "No life address data";

  return {
    score: Math.min(totalScore, MAX_TOTAL),
    max: MAX_TOTAL,
    label: "Life Address Match",
    detail,
    breakdown,
  };
}

// ── MAIN EXPORT ───────────────────────────────────────────────────────────────

/**
 * computeMatchScore(listing, buyerProfile, options)
 *
 * @param {object} listing        — listing data from HomeCard/Details (value prop)
 * @param {object} buyerProfile   — from context.state.buyerHomeDetails
 * @param {object} options        — optional overrides:
 *   listingCoords: { lat, lng }  — if already geocoded (from Details.js state)
 *   drivingDistances: object     — map of distKey → miles from Distance Matrix
 *   includeLifeAddresses: bool   — whether to compute Tier 2 (default false on cards)
 *
 * @returns {object} {
 *   total: number,       — 0-90 (without AI layer)
 *   tier1: number,       — 0-80 (house criteria)
 *   tier2: number,       — 0-20 (life addresses, 0 if not computed)
 *   percent: number,     — total as a percentage string e.g. "78%"
 *   breakdown: array,    — per-category scores for UI rendering
 *   hasLifeAddresses: bool,
 *   hasBuyerProfile: bool,
 * }
 */
function computeMatchScore(listing, buyerProfile, options = {}) {
  const {
    listingCoords = null,
    drivingDistances = null,
    includeLifeAddresses = false,
  } = options;

  const noProfile = {
    total: null, tier1: null, tier2: null,
    percent: null, breakdown: [], hasLifeAddresses: false, hasBuyerProfile: false,
  };

  if (!buyerProfile) return noProfile;
  if (!listing) return noProfile;

  // Support both nested (from context) and flat (pre-merged) profiles
  var isFlat = !buyerProfile.buyerPropertyDetails;
  const propDetails = isFlat ? buyerProfile : (buyerProfile.buyerPropertyDetails || {});
  const propFeatures = isFlat ? buyerProfile : (buyerProfile.buyerPropertyFeatures || {});
  const lifeAddresses = propDetails.lifeAddresses || buyerProfile.lifeAddresses || [];

  if (!isFlat && !buyerProfile.buyerPropertyDetails) return noProfile;

  // Merge profile fields — they live in different sub-objects
  const profile = { ...propDetails, ...propFeatures };

  // Tier 1 — house criteria
  const priceResult = scorePriceTier1(listing, profile);
  const bedsResult = scoreBedsTier1(listing, profile);
  const bathsResult = scoreBathsTier1(listing, profile);
  const areaResult = scoreLivingAreaTier1(listing, profile);
  const typeResult = scorePropertyTypeTier1(listing, profile);
  const cityResult = scoreCityTier1(listing, profile);

  const tier1Total = priceResult.score + bedsResult.score + bathsResult.score +
    areaResult.score + typeResult.score + cityResult.score;

  // Tier 2 — life addresses (only when explicitly requested, e.g. on detail page)
  let tier2Result = { score: 0, max: 20, breakdown: [] };
  if (includeLifeAddresses && lifeAddresses.length > 0) {
    tier2Result = scoreLifeAddressesTier2(listingCoords, lifeAddresses, drivingDistances);
  }

  const total = tier1Total + tier2Result.score;
  const percent = Math.round(total) + "%";

  return {
    total,
    tier1: tier1Total,
    tier2: tier2Result.score,
    percent,
    breakdown: [
      priceResult,
      bedsResult,
      bathsResult,
      areaResult,
      typeResult,
      cityResult,
      ...(includeLifeAddresses && lifeAddresses.length > 0 ? [tier2Result] : []),
    ],
    hasLifeAddresses: lifeAddresses.length > 0,
    hasBuyerProfile: true,
    tier2Breakdown: tier2Result.breakdown || [],
  };
}

// ── Score color helper (for badge rendering) ──────────────────────────────────
function getScoreColor(score) {
  if (score >= 80) return "#1A5C40"; // sage green — great match
  if (score >= 60) return "#D4921A"; // gold — good match
  if (score >= 40) return "#2A3390"; // navy — fair match
  return "#888";                     // grey — low match
}

// ── Score label helper ────────────────────────────────────────────────────────
function getScoreLabel(score) {
  if (score >= 80) return "Great Match";
  if (score >= 60) return "Good Match";
  if (score >= 40) return "Fair Match";
  return "Low Match";
}

export { computeMatchScore, getScoreColor, getScoreLabel, haversineDistance };
export default computeMatchScore;